import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { FileUploader } from "react-drag-drop-files";

import { faUpload } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";
import { Radio } from "@/components/Radio/Radio";
import { BlacklistImportScope } from "@/enum/blacklist.enum";

import blacklistExample from "@/assets/example-blacklist.csv";

import { UploadBlacklistCsvState } from "./useUploadBlacklistCsv";

const downloadExample = () => {
	const link = document.createElement("a");

	link.href = blacklistExample;
	link.download = "blacklist-example.csv";
	link.click();
};

/** Rendered in this order, so the widest-reaching option is not the one under the cursor. */
const SCOPE_OPTIONS: { value: BlacklistImportScope; labelKey: string }[] = [
	{ value: BlacklistImportScope.BOTH, labelKey: "scopeBoth" },
	{ value: BlacklistImportScope.PERSON, labelKey: "scopePerson" },
	{ value: BlacklistImportScope.ORGANIZATION, labelKey: "scopeOrganization" },
];

/**
 * The body of both blacklist upload modals: example download, what to import from the file,
 * drop zone, the accepted-format hint, and whatever the last attempt produced (an error or an
 * import summary).
 */
export const BlacklistCsvUploadFields: FunctionComponent<{ upload: UploadBlacklistCsvState }> = ({
	upload,
}) => {
	const { t } = useTranslation();
	const tu = (key: string, options?: Record<string, unknown>) =>
		t(`blacklist.upload.${key}`, options);

	const { file, error, result, scope } = upload;

	return (
		<div className="flex flex-col gap-4">
			<Button
				color={ButtonColor.ACTION_SECONDARY}
				title={tu("downloadExample")}
				onClick={downloadExample}
			/>

			<fieldset className="flex flex-col gap-2">
				<legend className="text-sm font-medium text-text-primary mb-2">{tu("scopeLabel")}</legend>

				{SCOPE_OPTIONS.map((option) => (
					<Radio
						key={option.value}
						isChecked={scope === option.value}
						name="blacklist-import-scope"
						testId={`blacklist-import-scope-${option.value}`}
						value={option.value}
						onChange={() => upload.selectScope(option.value)}
					>
						{tu(option.labelKey)}
					</Radio>
				))}

				<p className="text-xs text-text-meta">{tu("scopeHint")}</p>
			</fieldset>

			<FileUploader
				handleChange={upload.selectFile}
				name="file"
				types={["csv"]}
				onSizeError={() => upload.rejectFile("size")}
				onTypeError={() => upload.rejectFile("type")}
			>
				<div className="flex flex-col items-center justify-center w-full cursor-pointer border-2 border-dotted border-border-light gap-4 p-16 rounded-xl whitespace-nowrap">
					<Icon className="text-text-meta w-8 h-8" icon={faUpload} />
					<span className="text-sm text-text-meta">{file?.name || tu("dragAndDrop")}</span>
				</div>
			</FileUploader>

			<p className="text-xs text-text-meta">{tu("formatHint")}</p>

			{error ? (
				<p className="text-sm text-status-error" role="alert">
					{error}
				</p>
			) : null}

			{result ? (
				<div className="flex flex-col gap-1 text-xs text-text-meta">
					<span>
						{tu("summary", {
							imported: result.imported,
							total: result.totalRows,
							persons: result.importedPersons,
							organizations: result.importedOrganizations,
							duplicates: result.duplicates,
							skipped: result.skipped,
						})}
					</span>

					{result.unknownColumns.length ? (
						<span>{tu("ignoredColumns", { columns: result.unknownColumns.join(", ") })}</span>
					) : null}

					{result.errors.map((rowError) => (
						<span key={`${rowError.row}-${rowError.reason}`}>
							{tu("rowError", { row: rowError.row, reason: rowError.reason })}
						</span>
					))}
				</div>
			) : null}
		</div>
	);
};
